
import { createClient } from 'contentful'

const SPACE_ID = process.env.REACT_APP_CONTENTFUL_SPACE_ID
const ACCESS_TOKEN = process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN

let client
if (SPACE_ID && ACCESS_TOKEN) {
  client = createClient({
    space: SPACE_ID,
    accessToken: ACCESS_TOKEN,
  })
}

function richTextToPlain(node) {
  if (!node) return ''
  // node may be a root document or text node
  if (node.nodeType === 'text') return node.value || ''
  if (Array.isArray(node)) return node.map(richTextToPlain).join(' ')
  if (node.content && Array.isArray(node.content)) {
    return node.content.map(richTextToPlain).join(' ')
  }
  return ''
}

export async function fetchFeedEntries({ contentType = 'myTechActivities', limit = 100 } = {}) {
  if (!client) {
    throw new Error('Contentful client not configured. Set REACT_APP_CONTENTFUL_SPACE_ID and REACT_APP_CONTENTFUL_ACCESS_TOKEN')
  }

  const res = await client.getEntries({ content_type: contentType, limit })
  // map the items to a simpler shape tailored to `myTechActivities` content type
  const mapped = res.items.map((it) => {
    const fields = it.fields || {}
    const summary = fields.activityDescription ? richTextToPlain(fields.activityDescription) : (fields.summary || fields.description || '')
    const impact = fields.activityImpact ? richTextToPlain(fields.activityImpact) : undefined
    const coverImageUrl = fields.coverImage?.fields?.file?.url ? `https:${fields.coverImage.fields.file.url}` : undefined
    const mediaUrls = Array.isArray(fields.media)
      ? fields.media.map((m) => (m?.fields?.file?.url ? `https:${m.fields.file.url}` : undefined)).filter(Boolean)
      : []

    return {
      id: it.sys?.id,
      title: fields.activityName || fields.title,
      summary,
      impact,
      // keep raw date strings as provided by Contentful (ISO format)
      startDate: fields.startDate || null,
      endDate: fields.endDate || null,
      category: fields.category || [],
      coverImage: coverImageUrl,
      media: mediaUrls,
      relevantPublicLinks: fields.relevantPublicLinks || [],
      relevantPrivateLinks: fields.relevantPrivateLinks || [],
      // keep original fields for flexibility
      _raw: fields,
    }
  })

  // sort by endDate (newest first). If no endDate, treat as older.
  mapped.sort((a, b) => {
    const aTime = a.endDate ? new Date(a.endDate).getTime() : -Infinity
    const bTime = b.endDate ? new Date(b.endDate).getTime() : -Infinity
    return bTime - aTime
  })

  return mapped
}

export async function fetchTechResources({ limit = 100 } = {}) {
  if (!client) {
    throw new Error('Contentful client not configured. Set REACT_APP_CONTENTFUL_SPACE_ID and REACT_APP_CONTENTFUL_ACCESS_TOKEN')
  }

  const res = await client.getEntries({ content_type: 'techResources', limit })
  
  const mapped = res.items.map((it) => {
    const fields = it.fields || {}
    const description = typeof fields.description === 'string' ? fields.description : richTextToPlain(fields.description)
    return {
      id: it.sys?.id,
      title: fields.title || fields.name || '',
      description: description || '',
      link: fields.link || fields.url || '#',
      category: fields.resourceCategory || fields.category || [],
      _raw: fields,
    }
  })
  return mapped
}

export async function fetchResourceById(id) {
  if (!client) {
    throw new Error('Contentful client not configured.')
  }
  const res = await client.getEntry(id).catch(e => {
    console.warn('Resource not found for id:', id);
    return null;
  });
  
  if (res) {
    const fields = res.fields || {}
    const description = typeof fields.description === 'string' ? fields.description : richTextToPlain(fields.description)
    return {
      id: res.sys?.id,
      title: fields.title || fields.name || '',
      description: description || '',
      link: fields.link || fields.url || '#',
      category: fields.resourceCategory || fields.category || [],
      _raw: fields,
    }
  }
  return null
}

export async function fetchTechProducts({ limit = 100 } = {}) {
  if (!client) {
    throw new Error('Contentful client not configured. Set REACT_APP_CONTENTFUL_SPACE_ID and REACT_APP_CONTENTFUL_ACCESS_TOKEN')
  }

  const res = await client.getEntries({ content_type: 'myTechProducts', limit })
  
  const mapped = res.items.map((it) => {
    const fields = it.fields || {}
    const description = typeof fields.description === 'string' ? fields.description : richTextToPlain(fields.description)
    const imageUrl = fields.image?.fields?.file?.url ? `https:${fields.image.fields.file.url}` : undefined
    
    return {
      id: it.sys?.id,
      title: fields.title || fields.name || '',
      description: description || '',
      link: fields.link || fields.url || '#',
      image: imageUrl,
      category: fields.category || [],
      _raw: fields,
    }
  })
  return mapped
}

export default client
